import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Dropdown from "../common/Dropdown";
import { Tooltip } from "@mui/material";
import { getSoundLibraryAI, getSoundLibraryAITypes, getFilteredSoundLibraryAICategories } from "../../api/soundLibrary";
import { SoundLibraryAI } from "../../types/types";
import { SoundLibraryAITypeResponse, SoundLibraryAICategoryResponse } from "../../types/requestsOutputs";
import AudioPlayerRewind from "../common/AudioPlayer";
import { useLanguage } from "../../context/LanguageContext";
import Icon from "../common/Icon";
import { useNotification } from "../../context/NotificationContext";
import styles from "./SoundLibraryModalList.module.css";
import paginationStyles from "../common/Pagination.module.css";
import adminStyles from "../../pages/administration/SoundLibrary/SoundLibraryAdmin.module.css";

interface SoundLibraryModalListProps {
    onClick: (item: SoundLibraryAI) => void;
    search?: string;
}

const SoundLibraryModalList: React.FC<SoundLibraryModalListProps> = ({ onClick, search = "" }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { language } = useLanguage();
    const { showNotification } = useNotification();

    const [library, setLibrary] = useState<SoundLibraryAI[]>([]);
    const [types, setTypes] = useState<SoundLibraryAITypeResponse[]>([]);
    const [categories, setCategories] = useState<SoundLibraryAICategoryResponse[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [typesLoading, setTypesLoading] = useState<boolean>(true);
    const [categoriesLoading, setCategoriesLoading] = useState<boolean>(true);
    const [deleteLoading] = useState<string | null>(null);

    const [searchDebounced, setSearchDebounced] = useState<string>("");
    const [selectedType, setSelectedType] = useState<string>("");
    const [selectedCategory, setSelectedCategory] = useState<string>("");
    const [selectedSound, setSelectedSound] = useState<SoundLibraryAI | null>(null);

    const [page, setPage] = useState<number>(1);
    const [limit] = useState<number>(10);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [totalItems, setTotalItems] = useState<number>(0);

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchDebounced(search);
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        setPage(1);
        setSelectedSound(null);
    }, [searchDebounced, selectedType, selectedCategory]);

    const fetchLibrary = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token") || "";
            const response = await getSoundLibraryAI({
                token,
                language,
                searchQuery: searchDebounced,
                includeRewind: true,
                typeId: selectedType,
                categoryId: selectedCategory,
                page,
                limit,
            });

            setLibrary(response.items);
            setTotalItems(response.total);
            setTotalPages(response.totalPages);
        } catch (error) {
            console.error("Error fetching sound library:", error);
            showNotification({
                message: t("errorFetchingSoundLibrary"),
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    }, [language, selectedType, selectedCategory, searchDebounced, showNotification, t, page, limit]);

    const fetchCategories = useCallback(async () => {
        try {
            setCategoriesLoading(true);
            const token = localStorage.getItem("token") || "";
            const filteredCategories = await getFilteredSoundLibraryAICategories({
                token,
                language,
                includeRewind: true,
                searchQuery: searchDebounced,
                typeId: selectedType || null,
            });
            setCategories(filteredCategories);
        } catch (err) {
            console.error("Failed to fetch categories", err);
            showNotification({
                message: t("errorFetchingSoundCategories"),
                type: "error",
            });
        } finally {
            setCategoriesLoading(false);
        }
    }, [language, searchDebounced, selectedType, showNotification, t]);

    useEffect(() => {
        fetchLibrary();

        const token = localStorage.getItem("token") || "";

        setTypesLoading(true);
        getSoundLibraryAITypes({ token, language })
            .then(setTypes)
            .catch((err) => {
                console.error("Failed to fetch types", err);
                showNotification({
                    message: t("errorFetchingSoundTypes"),
                    type: "error",
                });
            })
            .finally(() => setTypesLoading(false));
    }, [fetchLibrary, language, showNotification, t]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    const visiblePages = useMemo(() => {
        const range = [];

        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) range.push(i);
        } else {
            range.push(1);
            if (page > 3) range.push("...");
            for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
                range.push(i);
            }
            if (page < totalPages - 2) range.push("...");
            range.push(totalPages);
        }

        return range;
    }, [page, totalPages]);

    const startItem = (page - 1) * limit + 1;
    const endItem = Math.min(page * limit, totalItems);

    const typeOptions = useMemo(
        () =>
            types.map((type) => ({
                value: type.id,
                label: type.localizedName,
            })),
        [types],
    );

    const categoryOptions = useMemo(
        () =>
            categories.map((cat) => ({
                value: cat.id,
                label: cat.localizedName,
            })),
        [categories],
    );

    useEffect(() => {
        if (selectedCategory && !categoryOptions.some((option) => option.value === selectedCategory)) {
            setSelectedCategory("");
        }
    }, [selectedCategory, categoryOptions]);

    return (
        <div className={`${styles.container}${selectedSound ? ` ${styles.containerWithPlayer}` : ""}`}>
            <div className={styles.filtersContainer}>
                <div className={styles.filtersRow}>
                    <div className={styles.filterDropdown}>
                        <Dropdown
                            label={t("type") || t("filterByType")}
                            options={typeOptions}
                            value={selectedType}
                            onChange={setSelectedType}
                            placeholder={t("filterByType")}
                            loading={typesLoading}
                            error={false}
                            isWithoutMargin={true}
                        />
                    </div>
                    <div className={styles.filterDropdown}>
                        <Dropdown
                            label={t("category") || t("filterByCategory")}
                            options={categoryOptions}
                            value={selectedCategory}
                            onChange={setSelectedCategory}
                            placeholder={t("filterByCategory")}
                            loading={categoriesLoading}
                            error={false}
                            isWithoutMargin={true}
                        />
                    </div>
                </div>
            </div>

            {loading || deleteLoading ? (
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                </div>
            ) : library.length === 0 ? (
                <div className={adminStyles.noItemsMessage}>{t("noSoundsFound")}</div>
            ) : (
                <>
                    <div className={styles.listBody}>
                        {library.map((item, index) => (
                            <div key={item.id || index} className={styles.item}>
                                <button
                                    type="button"
                                    className={styles.play}
                                    onClick={() => setSelectedSound(item)}
                                    aria-label={t("play") || "Play"}
                                >
                                    <Icon name="ic-play" />
                                </button>
                                <div className={styles.name}>
                                    <Tooltip title={`${t("name")}: ${item.name}`} placement="bottom-start">
                                        <h4>{item.name}</h4>
                                    </Tooltip>
                                    <Tooltip
                                        title={`${t("description")}: ${item.soundDescription}`}
                                        placement="bottom-start"
                                    >
                                        <p>{item.soundDescription}</p>
                                    </Tooltip>
                                </div>
                                <div className={styles.meta}>
                                    <Tooltip title={`${t("type")}: ${item.type}`} placement="bottom-start">
                                        <p>{item.type}</p>
                                    </Tooltip>
                                </div>
                                <div className={styles.meta}>
                                    <Tooltip
                                        title={`${t("categories")}: ${item.categories?.map((c) => c.name).join(", ")}`}
                                        placement="bottom-start"
                                    >
                                        <p>{item.categories?.map((c) => c.name).join(", ")}</p>
                                    </Tooltip>
                                </div>
                                <div className={styles.editButtons}>
                                    <button type="button" className={styles.editButton} onClick={() => onClick(item)}>
                                        {t("add")}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className={styles.paginationFooter}>
                        <div className={paginationStyles.controls}>
                            <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                                &lt;
                            </button>

                            {visiblePages.map((p, idx) =>
                                p === "..." ? (
                                    <span key={`ellipsis-${idx}`} className="ellipsis">
                                        ...
                                    </span>
                                ) : (
                                    <button
                                        key={p}
                                        className={p === page ? "active" : ""}
                                        onClick={() => setPage(p as number)}
                                    >
                                        {p}
                                    </button>
                                ),
                            )}

                            <button disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>
                                &gt;
                            </button>

                            <span className="pagination-total">
                                {t("paginationCounter")}: {`${startItem}–${endItem} / ${totalItems}`}
                            </span>
                        </div>
                    </div>
                </>
            )}

            {selectedSound && (
                <div className={styles.playerFooter}>
                    <div className={styles.playerFooterInner}>
                        <AudioPlayerRewind
                            title={selectedSound.name}
                            src={selectedSound.url}
                            showAddtoProject={false}
                            hideDownload
                            isAutoPlay={true}
                            hideSeparator
                            embedded
                        />
                        <button
                            type="button"
                            className={styles.playerClose}
                            onClick={() => setSelectedSound(null)}
                            aria-label={t("close")}
                        >
                            <Icon name="ic-x" className="ic" size={18} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SoundLibraryModalList;
